import apiClient from '../api';

const getDocumentTypes = async () => {
    try {
        const response = await apiClient.get('/truck-documents/types');
        return response.data;
    } catch (error) {
        console.error('Error fetching document types:', error);
        throw error;
    }
};

const getAllDocuments = async (filters = {}) => {
    try {
        const response = await apiClient.get('/truck-documents', { params: filters });
        return response.data;
    } catch (error) {
        console.error('Error fetching documents:', error);
        throw error;
    }
};

const getTruckDocuments = async (truckId) => {
    try {
        const response = await apiClient.get(`/truck-documents/truck/${truckId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching documents for truck ${truckId}:`, error);
        throw error;
    }
};

const getDocumentById = async (id) => {
    try {
        const response = await apiClient.get(`/truck-documents/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching document ${id}:`, error);
        throw error;
    }
};

const createDocument = async (documentData, file) => {
    try {
        // If we have a file, use FormData
        if (file) {
            const formData = new FormData();
            
            // Add document data
            Object.keys(documentData).forEach(key => {
                if (documentData[key] !== undefined && documentData[key] !== null) {
                    formData.append(key, documentData[key]);
                }
            });
            
            // Add file
            formData.append('file', file);
            
            // Explicitly set multipart content-type to ensure proper handling
            const response = await apiClient.post('/truck-documents', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } else {
            // No file, just send JSON
            const response = await apiClient.post('/truck-documents', documentData);
            return response.data;
        }
    } catch (error) {
        console.error('Error creating document:', error);
        throw error;
    }
};

const updateDocument = async (id, documentData, file) => {
    try {
        // If we have a file, use FormData
        if (file) {
            const formData = new FormData();
            
            // Add document data
            Object.keys(documentData).forEach(key => {
                if (documentData[key] !== undefined && documentData[key] !== null) {
                    formData.append(key, documentData[key]);
                }
            });
            
            // Add file
            formData.append('file', file);
            
            const response = await apiClient.patch(`/truck-documents/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } else {
            // No file, just send JSON
            const response = await apiClient.patch(`/truck-documents/${id}`, documentData);
            return response.data;
        }
    } catch (error) {
        console.error(`Error updating document ${id}:`, error);
        throw error;
    }
};

const deleteDocument = async (id) => {
    try {
        const response = await apiClient.delete(`/truck-documents/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error deleting document ${id}:`, error);
        throw error;
    }
};

const getDocumentUrl = async (id) => {
    try {
        const response = await apiClient.get(`/truck-documents/${id}/url`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching document URL ${id}:`, error);
        throw error;
    }
};

const createSmartUploadBatch = async (files) => {
    const formData = new FormData();
    (files || []).forEach((file) => formData.append('files', file));
    const response = await apiClient.post('/truck-documents/smart-upload/batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
    });
    return response.data?.data || response.data;
};

const getSmartUploadBatch = async (batchId) => {
    const response = await apiClient.get(`/truck-documents/smart-upload/batch/${batchId}`);
    return response.data?.data || response.data;
};

const confirmSmartUploadBatch = async (batchId, items) => {
    const response = await apiClient.post(`/truck-documents/smart-upload/batch/${batchId}/confirm`, { items });
    return response.data?.data || response.data;
};

const discardSmartUploadItems = async (batchId, itemIds) => {
    const response = await apiClient.post(`/truck-documents/smart-upload/batch/${batchId}/discard`, { itemIds });
    return response.data?.data || response.data;
};

const createFleetWideDocument = async (documentData, file) => {
    if (!file) {
        throw new Error('A file is required for fleet-wide upload');
    }
    const formData = new FormData();
    Object.keys(documentData || {}).forEach((key) => {
        if (documentData[key] !== undefined && documentData[key] !== null && documentData[key] !== '') {
            formData.append(key, documentData[key]);
        }
    });
    formData.append('file', file);
    const response = await apiClient.post('/truck-documents/fleet-wide', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
    });
    return response.data;
};

const truckDocumentService = {
    getDocumentTypes,
    getAllDocuments,
    getTruckDocuments,
    getDocumentById,
    createDocument,
    updateDocument,
    deleteDocument,
    getDocumentUrl,
    createSmartUploadBatch,
    getSmartUploadBatch,
    confirmSmartUploadBatch,
    discardSmartUploadItems,
    createFleetWideDocument,
};

export default truckDocumentService; 